'use client';
import {getStrapiData} from '@/app/lib/api';
import {cn} from '@/lib/utils';
import {StrapiMenuItem} from '@/types/strapi';
import {useLocale, useTranslations} from 'next-intl';
import Link from 'next/link';
import {useEffect, useState} from 'react';
import NavigationMenu from './NavigationMenu';
import LocaleSwitcher from './tools/LocaleSwitcher';
export default function Navigation({menu = []}: {menu?: StrapiMenuItem[]}) {
  const t = useTranslations('Navigation');
  const lang = useLocale();
  const m = useTranslations('Manifest');
  const [isOpenSearchBar, setIsOpenSearchBar] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchTags, setSearchTags] = useState<string[]>([]);
  const [escaping, setEscaping] = useState(false);
  const [showHeader, setShowHeader] = useState(false);

  const searchQueryHandler = () => {
    // get search type at URL
    const searchType =
      new URLSearchParams(window.location.search).get('t') ?? 'pages';
    window.location.href = `/search?q=${searchQuery}&t=${searchType}`;
  };

  useEffect(() => {
    //
    const applyTag = async () => {
      const tags = await getStrapiData(lang, 'search-tags', {
        sort: 'priority:desc'
      });
      const data = tags.data;

      setSearchTags(data?.map((item: any) => item.title));
    };
    applyTag();

    // get GET params from URL
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('popup') !== '1') {
      setShowHeader(true);
    }
  }, []);

  return (
    showHeader && (
      <header tabIndex={-1} className={'relative z-50 bg-white common-layout'}>
        <a
          id="skipToMain"
          href="#main"
          className={cn(
            'absolute left-0 -top-[40px] focus:top-0',
            'text-black p-2 bg-white z-200',
            'transition-[top] duration-[0.3s] ease-in-out'
          )}
        >
          {t('skipToMain')}
        </a>
        {isOpenSearchBar && (
          <div className="fixed top-0 left-0 w-full h-full bg-black bg-opacity-50 z-50">
            <div
              className="flex justify-center items-center h-full "
              onClick={() => {
                setIsOpenSearchBar(false);
              }}
            >
              <div
                className="bg-white p-2 rounded-md flex flex-col items-center justify-center py-4  w-[70vw] max-w-[500px]"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <div className="flex flex-row items-center justify-center w-full">
                  <label
                    htmlFor="search-input"
                    className="flex items-center justify-center"
                  >
                    <div className="material-symbols outlined text-black ">
                      search
                    </div>
                  </label>
                  <input
                    className="border-none focus:outline-none w-full h-full pl-4"
                    id="search-input"
                    type="text"
                    placeholder={t('search')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      setEscaping(false);
                      if (e.key === 'Enter') {
                        searchQueryHandler();
                      }
                      if (e.key === 'Escape') {
                        if (searchQuery === '' || escaping) {
                          setIsOpenSearchBar(false);
                          setEscaping(false);
                          return;
                        }
                        setEscaping(true);
                      }
                    }}
                    onSubmit={searchQueryHandler}
                  />
                  {escaping && (
                    <span className="text-xs text-[#e31836] w-[120] px-2">
                      {t('escToClose')}
                    </span>
                  )}
                  <button
                    className={cn('p-0 flex items-center ', {
                      'opacity-0': searchQuery === ''
                    })}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSearchQuery('');
                      document.getElementById('search-input')?.focus();
                    }}
                  >
                    <div className={cn('material-symbols outlined text-black')}>
                      close
                    </div>
                  </button>
                </div>
                <div className="w-full mt-4 p-2 flex border-t border-gray-200 ">
                  <div className="flex flex-wrap gap-2 justify-start items-center w-full">
                    {searchTags.map((tag, i) => (
                      <button
                        key={i}
                        onClick={(e) => {
                          setSearchQuery(tag);
                          document.getElementById('search-input')?.focus();
                        }}
                        className="text-sm bg-gray-200 rounded-full px-3 py-1  cursor-pointer"
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                  <div className="flex items-center justify-end">
                    <button
                      className={cn(
                        'h-10 w-10 rounded-full  text-white text-sm flex justify-center items-center bg-[#e22319]',
                        {
                          'opacity-50': searchQuery === ''
                        }
                      )}
                      onClick={searchQueryHandler}
                    >
                      <div className="material-symbols outlined text-white">
                        send
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        <div
          className="container flex justify-between items-center pb-[15px] md:pb-0 pt-[5px] md:pt-0 px-[5px] md:px-0"
          style={{maxWidth: '1080px'}}
        >
          <div className="relative">
            <Link
              prefetch={false}
              href={'https://www.aswatson.com/' + (lang != 'en' ? lang : '')}
              className="text-white"
              locale={lang}
              id="site-logo"
            >
              <div className="my-[10px] md:my-[15px] md:pb-0 lg:pl-0 pointer-events-none">
                <img
                  className="max-w-[60vw] max-h-[60px] md:max-h-[80px] object-contain object-left"
                  src={`/img/asw-185-logo-${lang}.png`}
                  alt={m('name')}
                />
              </div>
            </Link>
            <Link
              prefetch={false}
              href={'https://185-anniversary.aswatson.com'}
              className={cn(
                'absolute top-0 right-0 bottom-0 z-2 aspect-[150/80]',
                'my-[10px] md:my-[15px] md:max-h-[80px] max-h-[60px]'
              )}
              aria-label="185 logo"
              locale={lang}
            ></Link>
          </div>
          <div>{/* search input box */}</div>
          <div className="w-[180px] md:w-fit flex items-center justify-end gap-4 pr-2 lg:pr-0">
            {false && (
              <div className="p-2 hidden md:block">
                <div className="w-full rounded-full bg-white flex items-center justify-between border border-[#ddd]">
                  <div className="material-symbols outlined text-gray-400 bg-transparent pl-[5px] text-[20px] py-1">
                    search
                  </div>
                  <input
                    type="text"
                    className="text-[#333] w-full bg-transparent pl-1 font-[100] text-[13px] w-[140px] placeholder-gray-400 border-none focus:outline-none"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key == 'Enter') {
                        if (lang == 'zh') {
                          window.location.href = `https://www.aswatson.com/zh/search/?s=${searchQuery}&submit=Search`;
                        } else {
                          window.location.href = `https://www.aswatson.com/search/?s=${searchQuery}&submit=Search`;
                        }
                      }
                    }}
                    style={{
                      fontFamily: '"Helvetica Neue",Helvetica,Arial,sans-serif'
                    }}
                    placeholder={t('search')}
                  />
                </div>
              </div>
            )}
            {
              <button
                onClick={() => {
                  setIsOpenSearchBar(!isOpenSearchBar);
                  // focus on search input box
                  setTimeout(() => {
                    document.getElementById('search-input')?.focus();
                  }, 100);
                }}
                className="material-symbols outlined cursor-pointer "
                id="search-btn"
                title={t('search')}
              >
                search
              </button>
            }
            <LocaleSwitcher />
          </div>
        </div>
        <NavigationMenu lang={lang} menu={menu} />
      </header>
    )
  );
}
